import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, Phone, ChevronDown, Search } from 'lucide-react';
import { NAV_LINKS, LOGO, CATEGORIES } from '../mock/mock';
import { useCart } from '../context/CartContext';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { count, setIsOpen } = useCart();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <>
      {/* Top announcement bar */}
      <div className="bg-[#c8102e] text-white text-xs md:text-sm font-cond tracking-wide overflow-hidden">
        <div className="container-fes flex items-center justify-between py-2">
          <span className="hidden md:inline uppercase">Free shipping on orders over $250 &middot; Proudly Australian owned &amp; operated</span>
          <span className="md:hidden uppercase">Free shipping over $250</span>
          <a href="tel:+61478886027" className="flex items-center gap-1.5 uppercase hover:opacity-80 transition-opacity">
            <Phone size={14} /> +61 478 886 027
          </a>
        </div>
      </div>

      <header className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${scrolled ? 'shadow-md' : 'shadow-sm'}`}>
        <div className="container-fes flex items-center justify-between py-3">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="FesMed EMS" className="h-16 w-auto object-contain" />
            <span className="font-head text-2xl md:text-3xl font-bold uppercase tracking-tight text-[#c8102e]">FesMed EMS</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <div key={link.path} className="relative group">
                <Link
                  to={link.path}
                  className={`font-cond uppercase tracking-wide font-semibold px-4 py-2 rounded transition-colors flex items-center gap-1 ${
                    location.pathname === link.path ? 'text-[#c8102e]' : 'text-[#141414] hover:text-[#c8102e]'
                  }`}
                >
                  {link.label}
                  {link.label === 'Shop' && <ChevronDown size={14} />}
                </Link>
                {link.label === 'Shop' && (
                  <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="bg-white shadow-xl rounded-lg border border-gray-100 w-64 p-2">
                      {CATEGORIES.map((cat) => (
                        <Link key={cat.id} to={`/shop?cat=${cat.id}`} className="block px-3 py-2.5 rounded hover:bg-[#f4f6fa] transition-colors">
                          <span className="font-cond font-semibold text-[#141414] block">{cat.name}</span>
                          <span className="text-xs text-gray-500">{cat.tagline}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button className="hidden md:flex items-center justify-center w-10 h-10 text-[#141414] hover:text-[#c8102e] transition-colors" aria-label="Search">
              <Search size={20} />
            </button>
            <button
              onClick={() => setIsOpen(true)}
              className="relative flex items-center justify-center w-10 h-10 text-[#141414] hover:text-[#c8102e] transition-colors"
              aria-label="Cart"
            >
              <ShoppingCart size={22} />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#c8102e] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <button className="lg:hidden flex items-center justify-center w-10 h-10 text-[#141414]" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-gray-100 bg-white">
            <nav className="container-fes py-3 flex flex-col">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-cond uppercase tracking-wide font-semibold py-3 border-b border-gray-50 ${
                    location.pathname === link.path ? 'text-[#c8102e]' : 'text-[#141414]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
